import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { mapDtoToGuideline } from 'shared/domain';
import { appendHttpParamsFromObject, TreetopPropertiesProvider } from 'shared/util-common';

import {
  Pagination,
  GuidelineDto,
  PublicLinkDto,
  PublicLinkFilter,
  PublicLinkSort,
  Guideline,
  GuidelineBasicInfo,
  GuidelineCodingPageQueryParams,
} from '@treetop/shared/model';

interface PaginatedResponse<T> extends Pagination {
  list: T[];
}

@Injectable({
  providedIn: 'root',
})
export class PublicLinksHttpService {
  private http = inject(HttpClient);

  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  public get(filter: PublicLinkFilter, pagination: Pagination, sorting: PublicLinkSort): Observable<PaginatedResponse<PublicLinkDto>> {
    let params = new HttpParams();

    params = appendHttpParamsFromObject(pagination, params);
    params = appendHttpParamsFromObject(filter, params);

    if (sorting.order !== null) {
      params = params.append('field', sorting.field);
      params = params.append('order', sorting.order);
    }

    return this.http.get<PaginatedResponse<PublicLinkDto>>(`${this.baseUrl}/public-links`, { params });
  }

  public add(publicLinks: PublicLinkDto[]): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/public-links`, publicLinks);
  }

  public delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/public-links/${id}`);
  }

  public getGuideline(slug: string): Observable<Guideline> {
    return this.http.get<GuidelineDto>(`${this.baseUrl}/public/${slug}`).pipe(map(mapDtoToGuideline));
  }

  public getGuidelineForReview(slug: string): Observable<Guideline> {
    return this.http.get<GuidelineDto>(`${this.baseUrl}/public/hash/${slug}`).pipe(map(mapDtoToGuideline));
  }

  public matching(params: GuidelineCodingPageQueryParams): Observable<GuidelineBasicInfo[]> {
    return this.http.get<GuidelineBasicInfo[]>(`${this.baseUrl}/public/matching`, {
      params: new HttpParams()
        .set('systemId', params.systemId)
        .set('versionId', params.version)
        .set('code', params.code)
        .set('organisationCode', params.organizationId),
    });
  }

  public trackView(uniqueId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/public/${uniqueId}/track-view`, null);
  }
}
